import type { EndpointDTO, TargetDTO } from '@devrobot/shared';
import { ExternalLink, Globe } from 'lucide-react';
import { useRecordVisit } from '../api/queries';
import { CopyButton } from './CopyButton';
import { MenuPopover } from './MenuPopover';
import { endpointLabels, endpointStatus, isBrowsable, orderHosts, type EndpointLabel, type EndpointTone } from '../lib/endpoints';

const SIZE = { sm: 'btn-sm', xs: 'btn-xs' } as const;

// Written out rather than assembled, so the tone is readable at the call site.
const LIGHT: Record<EndpointTone, string> = {
  success: 'status status-success',
  warning: 'status status-warning',
  error: 'status status-error',
  neutral: 'status status-neutral',
};

/**
 * One light per host. Colour alone never carries the meaning: the state is the element's
 * accessible name, and anything short of ready explains itself on hover. A native title rather
 * than a daisyUI tooltip, which would be clipped by the viewport edge this menu opens against.
 */
function StatusLight({ e }: { e: EndpointDTO }) {
  const s = endpointStatus(e);
  const name = s.detail ? `${s.label}. ${s.detail}` : s.label;
  return (
    <span className="inline-flex shrink-0" role="img" aria-label={name} title={name}>
      <span className={LIGHT[s.tone]} aria-hidden="true" />
    </span>
  );
}

function HostRow({ e, label, onOpen }: { e: EndpointDTO; label: EndpointLabel; onOpen: (e: EndpointDTO) => void }) {
  const text = (
    <span className="min-w-0 flex-1 truncate font-mono text-sm">
      <span className="font-medium">{label.lead}</span>
      <span className="text-base-content/50">{label.rest}</span>
    </span>
  );
  return (
    <li className="flex items-center gap-2 rounded-field px-2 py-1.5 hover:bg-base-200/60">
      <StatusLight e={e} />
      {isBrowsable(e) ? (
        <a href={e.url} target="_blank" rel="noreferrer" onClick={() => onOpen(e)} title={e.url} className="link link-hover flex min-w-0 flex-1 items-center gap-1">
          {text}
          <ExternalLink className="size-3 shrink-0 opacity-60" aria-hidden="true" />
        </a>
      ) : (
        <span title={e.url} className="flex min-w-0 flex-1 items-center">
          {text}
        </span>
      )}
      <CopyButton value={e.url} label={`Copy ${e.url}`} />
    </li>
  );
}

/**
 * Opens a deployment's public hosts. With several hosts it drops down a menu ordered by the ones
 * this user opened most recently; with a single host the button is the link itself.
 *
 * Each host is a real anchor and the visit is recorded without preventing the default: opening
 * a tab after awaiting a request would be treated as a pop-up and blocked.
 */
export function VisitButton({ target, size = 'sm', className = '' }: { target: TargetDTO; size?: keyof typeof SIZE; className?: string }) {
  const record = useRecordVisit();
  const name = `${target.project.name} · ${target.environment}`;
  const onOpen = (e: EndpointDTO) => record.mutate({ targetId: target.id, url: e.url });
  const cls = `btn btn-soft btn-primary ${SIZE[size]} ${className}`;

  if (target.endpoints.length === 0) {
    return (
      <button type="button" className={cls} disabled title="Nothing is exposed publicly">
        <Globe className="size-4" aria-hidden="true" /> Visit
      </button>
    );
  }

  // A one-item menu is pure friction: go straight there.
  const hosts = orderHosts(target.endpoints);
  if (hosts.length === 1 && isBrowsable(hosts[0])) {
    const only = hosts[0];
    return (
      <a href={only.url} target="_blank" rel="noreferrer" onClick={() => onOpen(only)} className={cls} aria-label={`Visit ${name} at ${only.url}`}>
        <ExternalLink className="size-4" aria-hidden="true" /> Visit
      </a>
    );
  }

  const labels = endpointLabels(hosts);
  return (
    <MenuPopover
      label={`Visit ${name}`}
      triggerClassName={cls}
      panelClassName="w-80 max-w-[calc(100vw-2rem)]"
      trigger={
        <>
          <Globe className="size-4" aria-hidden="true" /> Visit
          <span className="badge badge-ghost badge-xs">{hosts.length}</span>
        </>
      }
    >
      {() => (
        <ul>
          {hosts.map((e) => (
            <HostRow key={e.url} e={e} label={labels.get(e.url) ?? { lead: e.url, rest: '' }} onOpen={onOpen} />
          ))}
        </ul>
      )}
    </MenuPopover>
  );
}
